import { EMAIL_CENTER_ACTIONS } from '@/constants/actions';
import { SOLUTION_KEYS } from '@/constants/solutions';
import * as EmailCenterSelectors from '@/selectors/emailCenterSelectors';
import * as JourneySelectors from '@/selectors/journeySelectors';
import { useCallback, useEffect } from 'react';
import { useDispatch, useModel, useSelector } from 'umi';
import { useEmailCenterUrlState } from './useEmailCenterUrlState';

/**
 * Journey context for the journey-scoped Touchpoints surfaces. Every consumer
 * reads `emailCenter.selectedJourneyHashId`; this hook is what feeds it, and
 * where it feeds it from depends on the solution:
 *
 * - **Lead generation** has the sidebar journey accordion. That is the one
 *   journey selector a user should have to think about, so the pages follow it
 *   and offer no picker of their own — `?journey=` is kept out of the URL there
 *   so the two cannot drift apart. The accordion lists journeys only, so the
 *   other lead-source types are out of reach in this solution.
 * - **Lead management** has no global selector, so the page owns the choice: the
 *   `?journey=` param and the store stay in sync and `selectDefaultLeadSource`
 *   is selected when nothing is chosen yet.
 *
 * The list it offers is *every* lead source, not only the journeys: a CSV,
 * webhook or manual source carries a theme and sends emails of its own, so its
 * branding has to be editable too. A real journey still wins the default.
 */
/**
 * Whether the sidebar journey accordion is the selector of record.
 *
 * It is rendered only inside the lead-generation solution tab (see
 * `SolutionSwitch`), so that is the whole rule — kept here because more than one
 * hook has to answer the same question the same way.
 */
export function useJourneyFromSidebar(): boolean {
  const { initialState } = useModel('@@initialState');
  return initialState?.selectedSolution === SOLUTION_KEYS.LEAD_GENERATION;
}

export function useEmailCenterJourney() {
  const dispatch = useDispatch();
  const [urlState, setUrlState] = useEmailCenterUrlState();

  const journeys = useSelector(JourneySelectors.selectJourneys);
  const journeysInitialized = useSelector(
    JourneySelectors.selectJourneysInitialized,
  );
  const defaultJourney = useSelector(JourneySelectors.selectDefaultLeadSource);
  const journeyHashId = useSelector(
    EmailCenterSelectors.selectSelectedJourneyHashId,
  );
  const sidebarJourney = useSelector(JourneySelectors.selectActiveJourney);
  const sidebarHashId = sidebarJourney?.hashId ?? null;

  const journeyFromSidebar = useJourneyFromSidebar();

  useEffect(() => {
    if (!journeyFromSidebar) return;
    if (sidebarHashId && sidebarHashId !== journeyHashId) {
      dispatch({
        type: EMAIL_CENTER_ACTIONS.SELECT_JOURNEY,
        payload: sidebarHashId,
      });
    }
    // A `?journey=` left over from a link or a solution switch would outlive the
    // sidebar selection and quietly win on the next parse.
    if (urlState.journeyId) setUrlState({ journeyId: null });
  }, [
    journeyFromSidebar,
    sidebarHashId,
    journeyHashId,
    urlState.journeyId,
    dispatch,
    setUrlState,
  ]);

  useEffect(() => {
    if (journeyFromSidebar) return;
    const urlJourneyId = urlState.journeyId;
    const inThisPartner = (hashId: string | null) =>
      hashId != null && journeys.some((journey) => journey.hashId === hashId);
    const urlJourneyExists = inThisPartner(urlJourneyId);

    /**
     * The list has loaded and does not hold the URL's journey — so the id is not
     * this partner's. It is left over from *another* partner: the account switch
     * resets the store and reloads, but the previous `?journey=` rides along in
     * the URL, and cake's journey endpoint answers 200 for it regardless of the
     * current partner (see the tenancy note in docs/qa). Left unhandled it stuck:
     * none of the branches below matched, so the stale id stayed selected and the
     * Email Center scoped to a journey this partner does not own — "0 templates"
     * for a partner that has them. Guarded on `journeysInitialized` so a list that
     * has simply not arrived yet never drops a valid deep link.
     */
    const urlJourneyForeign =
      urlJourneyId != null && journeysInitialized && !urlJourneyExists;

    if (urlJourneyForeign) {
      const fallback = inThisPartner(journeyHashId)
        ? journeyHashId
        : defaultJourney?.hashId ?? null;
      if (fallback) {
        if (fallback !== journeyHashId) {
          dispatch({
            type: EMAIL_CENTER_ACTIONS.SELECT_JOURNEY,
            payload: fallback,
          });
        }
        if (fallback !== urlJourneyId) setUrlState({ journeyId: fallback });
      } else if (urlJourneyId) {
        setUrlState({ journeyId: null });
      }
    } else if (urlJourneyExists && urlJourneyId !== journeyHashId) {
      dispatch({
        type: EMAIL_CENTER_ACTIONS.SELECT_JOURNEY,
        payload: urlJourneyId,
      });
    } else if (!urlJourneyId && inThisPartner(journeyHashId)) {
      setUrlState({ journeyId: journeyHashId });
    } else if (!urlJourneyId && !inThisPartner(journeyHashId) && defaultJourney) {
      dispatch({
        type: EMAIL_CENTER_ACTIONS.SELECT_JOURNEY,
        payload: defaultJourney.hashId,
      });
      setUrlState({ journeyId: defaultJourney.hashId });
    }
  }, [
    journeyFromSidebar,
    urlState.journeyId,
    journeyHashId,
    journeys,
    journeysInitialized,
    defaultJourney,
    dispatch,
    setUrlState,
  ]);

  /**
   * Move to another journey. In lead generation that means moving the sidebar,
   * since it is the selector of record — the effect above then mirrors it back
   * into the Email Center store.
   */
  const setJourney = useCallback(
    (hashId: string) => {
      if (journeyFromSidebar) {
        dispatch({ type: 'journey/select', payload: { journeyId: hashId } });
        return;
      }
      dispatch({ type: EMAIL_CENTER_ACTIONS.SELECT_JOURNEY, payload: hashId });
      setUrlState({ journeyId: hashId });
    },
    [journeyFromSidebar, dispatch, setUrlState],
  );

  return {
    journeys,
    journeyHashId,
    setJourney,
    /** True when the sidebar owns the choice, so pages hide their own picker. */
    journeyFromSidebar,
  };
}
